import { useEffect, useRef, type CSSProperties } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { App } from 'antd'
import {
  CloseOutlined,
  PlusSquareOutlined,
} from '@ant-design/icons'
import { useShallow } from 'zustand/react/shallow'
import { brandModalConfirmProps } from '@/components/BrandButton'
import {
  useAppStore,
  useUploadDrawerStore,
  useUploadStore,
  useVersionDrawerStore,
} from '@/store'
import { DEFAULT_APP_ICON } from '@/lib/appIcon'
import type { UploadTask } from '@/types'
import { platformLabel } from './platform'

const isActive = (x: UploadTask) =>
  x.status === 'uploading' || x.status === 'parsing'

/**
 * 子包后台进度浮层：主包只在上传抽屉内上传，不进浮层。
 * 任一抽屉打开时隐藏（抽屉内已有行内进度）。
 */
export default function UploadProgressPanel() {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    allTasks,
    collapsed,
    setCollapsed,
    cancelTask,
    retryTask,
    clear,
  } = useUploadStore(
    useShallow((s) => ({
      allTasks: s.tasks,
      collapsed: s.collapsed,
      setCollapsed: s.setCollapsed,
      cancelTask: s.cancelTask,
      retryTask: s.retryTask,
      clear: s.clear,
    })),
  )
  const tasks = allTasks.filter((x) => x.kind === 'platform')
  const uploadDrawerOpen = useUploadDrawerStore((s) => s.open)
  const versionDrawerOpen = useVersionDrawerStore((s) => s.open)
  const openVersion = useVersionDrawerStore((s) => s.openVersion)

  // 会话只属于一份草稿：一律用任务自己记下的应用 / 版本，不看当前路由
  const target_app_id = tasks[0]?.app_id ?? null
  const target_version_id = tasks[0]?.version_id ?? null
  const appIcon =
    useAppStore((s) =>
      target_app_id ? s.apps.find((a) => a.id === target_app_id)?.icon : undefined,
    ) || DEFAULT_APP_ICON
  const toasted = useRef(new Set<string>())
  const drawerOpen = uploadDrawerOpen || versionDrawerOpen
  const anyActive = tasks.some(isActive)

  useEffect(() => {
    if (drawerOpen) return
    for (const task of tasks) {
      if (task.status !== 'failed' && task.status !== 'parse_failed') continue
      const key = `${task.id}:${task.error_message ?? ''}`
      if (toasted.current.has(key)) continue
      toasted.current.add(key)
      message.error(
        task.error_message?.trim() || t('versionForm.uploadFailed'),
      )
    }
  }, [tasks, drawerOpen, message, t])

  useEffect(() => {
    if (!anyActive) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [anyActive])

  if (drawerOpen) return null
  if (tasks.length === 0) return null

  const anyFailed = tasks.some(
    (x) => x.status === 'failed' || x.status === 'parse_failed',
  )
  const allSuccess = tasks.every((x) => x.status === 'success')
  const avgProgress = Math.round(
    tasks.reduce((s, x) => s + x.progress, 0) / tasks.length,
  )

  const title = anyFailed
    ? t('uploadPanel.partiallyFailed')
    : allSuccess
      ? t('uploadPanel.allSuccess')
      : t('uploadPanel.uploading')

  const goConfigure = () => {
    if (!target_app_id || !target_version_id) return
    const target = `/apps/${target_app_id}/versions`
    if (location.pathname !== target) navigate(target)
    openVersion(target_app_id, target_version_id)
  }

  /** 展开态关闭：上传中只收成胶囊，不中断 */
  const closeExpanded = () => {
    if (anyActive) {
      setCollapsed(true)
      return
    }
    clear()
  }

  /** 胶囊关闭：上传中需确认后中断 */
  const closePill = () => {
    if (!anyActive) {
      clear()
      return
    }
    modal.confirm({
      title: t('versionForm.closeWhileUploadingTitle'),
      content: t('versionForm.closeWhileUploadingContent'),
      okText: t('versionForm.closeWhileUploadingOk'),
      cancelText: t('common.cancel'),
      ...brandModalConfirmProps('danger'),
      onOk: () => {
        if (useUploadStore.getState().tasks.some(isActive)) clear()
      },
    })
  }

  if (collapsed) {
    return (
      <div style={pillStyle}>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {t('uploadPanel.collapsed', {
            count: tasks.length,
            percent: avgProgress,
          })}
        </span>
        <button type="button" style={iconBtn} onClick={() => setCollapsed(false)}>
          <PlusSquareOutlined />
        </button>
        <button type="button" style={iconBtn} onClick={closePill}>
          <CloseOutlined />
        </button>
      </div>
    )
  }

  return (
    <div style={glassStyle}>
      <div style={cardStyle}>
        <div>
          <div style={headerStyle}>
            <span
              style={{
                flex: 1,
                fontWeight: 500,
                fontSize: 14,
                lineHeight: '22px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {title}
            </span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '0 8px 0 10px',
                height: 22,
              }}
            >
              <button
                type="button"
                style={iconBtn}
                onClick={() => setCollapsed(true)}
                aria-label="collapse"
              >
                <span style={minimizeIcon} />
              </button>
              <button
                type="button"
                style={iconBtn}
                onClick={closeExpanded}
                aria-label="close"
              >
                <CloseOutlined style={{ fontSize: 12, lineHeight: 1 }} />
              </button>
            </div>
          </div>
          <div style={dividerStyle} />
        </div>

        <div style={bodyStyle}>
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              icon={appIcon}
              onCancel={() => cancelTask(task.id)}
              onRetry={() => retryTask(task.id)}
            />
          ))}

          {target_app_id && target_version_id && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" style={configureBtn} onClick={goConfigure}>
                {t('uploadPanel.continueConfigure')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TaskRow({
  task,
  icon,
  onCancel,
  onRetry,
}: {
  task: UploadTask
  icon: string
  onCancel: () => void
  onRetry: () => void
}) {
  const { t } = useTranslation()
  const label =
    task.kind === 'platform' && task.platform
      ? `${platformLabel(task.platform)} · ${task.file_name}`
      : task.file_name
  const iconIsUrl =
    /^(https?:|data:|blob:|\/|\.{1,2}\/)/i.test(icon) ||
    /\.(png|jpe?g|gif|svg|webp|avif)(\?.*)?$/i.test(icon)

  const subtitle = (
    <>
      <span>{Math.round(task.loaded_mb)}MB</span>
      <span style={{ color: 'rgba(0,0,0,0.4)' }}>
        {' '}
        / {task.size_mb} MB
      </span>
    </>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={fileIconStyle}>
          {iconIsUrl ? (
            <img
              src={icon}
              alt=""
              width={48}
              height={48}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            icon
          )}
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <div
            title={label}
            style={{
              fontSize: 14,
              fontWeight: 500,
              lineHeight: '22px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </div>
          <div style={{ fontSize: 14, lineHeight: '22px' }}>{subtitle}</div>
        </div>
      </div>

      {task.status === 'uploading' && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              paddingRight: 6,
            }}
          >
            <div
              style={{
                flex: 1,
                height: 6,
                borderRadius: 8,
                background: 'rgba(140, 140, 140, 0.16)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${task.progress}%`,
                  height: '100%',
                  borderRadius: 8,
                  background: 'var(--accent)',
                  transition: 'width 0.2s',
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 500, lineHeight: '18px' }}>
                {task.progress}%
              </span>
              {task.speed && (
                <span style={{ fontSize: 12, lineHeight: '18px' }}>
                  {task.speed}
                </span>
              )}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                lineHeight: '20px',
                color: 'rgba(0,0,0,0.6)',
              }}
            >
              {task.remaining_sec === 0
                ? t('uploadPanel.remainingSoon')
                : t('uploadPanel.remaining', {
                    time: formatRemaining(task.remaining_sec, t),
                  })}
            </span>
            <button type="button" style={linkBtn} onClick={onCancel}>
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}

      {(task.status === 'failed' || task.status === 'parse_failed') && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" style={linkBtn} onClick={onRetry}>
            {t('versionForm.retry')}
          </button>
        </div>
      )}
    </div>
  )
}

function formatRemaining(sec: number | undefined, t: TFunction): string {
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return '—'
  const total = Math.round(sec)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) {
    return m > 0
      ? t('uploadPanel.remainingHourMin', { h, m })
      : t('uploadPanel.remainingHour', { h })
  }
  if (m > 0) {
    return s > 0
      ? t('uploadPanel.remainingMinSec', { m, s })
      : t('uploadPanel.remainingMin', { m })
  }
  return t('uploadPanel.remainingSec', { s })
}

const glassStyle: CSSProperties = {
  position: 'fixed',
  right: 20,
  bottom: 20,
  width: 360,
  borderRadius: 14,
  background: 'var(--color-background-glass)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxShadow: '0px 16px 22px -6px rgba(28, 28, 28, 0.16)',
  zIndex: 1200,
}

const cardStyle: CSSProperties = {
  background: 'var(--color-background-module-container)',
  border: '1px solid var(--color-border-line)',
  borderRadius: 14,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  paddingBottom: 20,
}

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 42,
  padding: '10px 12px 10px 20px',
  boxSizing: 'border-box',
}

const fileIconStyle: CSSProperties = {
  width: 48,
  height: 48,
  flexShrink: 0,
  borderRadius: 14,
  background: 'var(--color-background-module-container)',
  boxShadow: '0 2px 8px rgb(0 0 0 / 0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 28,
  overflow: 'hidden',
}

const dividerStyle: CSSProperties = {
  height: 1,
  background: 'rgba(0,0,0,0.04)',
}

const bodyStyle: CSSProperties = {
  padding: '0 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
}

const pillStyle: CSSProperties = {
  position: 'fixed',
  right: 20,
  bottom: 20,
  width: 260,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  background: 'var(--color-background-module-container)',
  border: '1px solid var(--color-border-line)',
  borderRadius: 999,
  boxShadow: '0px 16px 22px -6px rgba(28, 28, 28, 0.16)',
  zIndex: 1200,
  fontSize: 13,
}

const iconBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: '#000',
  padding: 0,
  margin: 0,
  width: 16,
  height: 16,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  flexShrink: 0,
}

/** Figma 收起图标：10×1.5 横线 */
const minimizeIcon: CSSProperties = {
  display: 'block',
  width: 10,
  height: 1.5,
  background: '#000',
  borderRadius: 1,
}

const linkBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--color-brand-indigo)',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: '20px',
  padding: '4px 8px',
  flexShrink: 0,
}

const configureBtn: CSSProperties = {
  height: 32,
  padding: '6px 18px',
  borderRadius: 6,
  border: '1px solid var(--color-border-line)',
  background: 'transparent',
  backdropFilter: 'blur(6px)',
  cursor: 'pointer',
  fontSize: 13,
  lineHeight: '20px',
  color: '#000',
}
